"use client";

import { c as _c } from "react/compiler-runtime";
import type { ColumnDefinition } from "@xefi/x-react/datagrid";
import { IconHistory, IconPencil } from "@xefi/x-react/icons";
import { useTranslation } from "react-i18next";
import type { NationalTransferModel } from "../../../../domain/models/NationalTransfer";
import { usePermissions } from "@/hooks/usePermissions";
import { CellTooltip } from "../common/CellTooltip";
import { formatAccountLabel, formatPaymentDate, formatStringAmount, formatText, isArchived, isDone } from "../common/paymentsFormat";
import type { EditableField } from "./NationalTransferRowEditor";
type EditControls = {
  onOpenEditor: (row: NationalTransferModel, field: EditableField, anchor: Element) => void;
  onOpenHistory: (row: NationalTransferModel) => void;
};
const WRAPPED_CELL = "h-auto max-h-none whitespace-normal py-1.5 align-top";
const WRAPPED_TEXT = "line-clamp-3 whitespace-normal text-xs leading-snug text-black dark:text-white";
export const useNationalTransfersColumns = t0 => {
  const $ = _c(45);
  const {
    onOpenEditor,
    onOpenHistory
  } = t0;
  const {
    t
  } = useTranslation();
  const {
    hasRole
  } = usePermissions();
  let t1;
  if ($[0] !== t) {
    t1 = field => t(`paymentsReceived.nationalTransfers.columns.${field}`);
    $[0] = t;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const label = t1;
  const textCell = _temp;
  const editableCell = _temp2;
  let t2;
  if ($[2] !== label || $[3] !== onOpenEditor) {
    t2 = (field_0, widthClass, display) => ({
      header: label(field_0),
      field: field_0,
      sortable: true,
      className: widthClass,
      cellClassName: _temp3,
      onClick: (row_0, event) => {
        if (isArchived(row_0)) {
          return;
        }
        onOpenEditor(row_0, field_0, event.currentTarget);
      },
      cell: row_1 => editableCell(display(row_1), !isArchived(row_1))
    });
    $[2] = label;
    $[3] = onOpenEditor;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const buildEditableColumn = t2;
  let t3;
  if ($[5] !== label) {
    t3 = (field_1, widthClass_0, render, t4) => {
      const sortable = t4 === undefined ? true : t4;
      return {
        header: label(field_1),
        field: field_1,
        sortable,
        className: widthClass_0,
        cell: row_2 => textCell(render(row_2))
      };
    };
    $[5] = label;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const buildReadonlyColumn = t3;
  let t4;
  if ($[7] !== t) {
    t4 = row_3 => isDone(row_3.to_do) ? t("paymentsReceived.yes") : t("paymentsReceived.no");
    $[7] = t;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const doneLabel = t4;
  let t5;
  if ($[9] !== buildEditableColumn || $[10] !== buildReadonlyColumn || $[11] !== doneLabel || $[12] !== hasRole) {
    t5 = !hasRole(["Applicant", "Viewer"]) ? buildEditableColumn("to_do", "w-[7%]", doneLabel) : buildReadonlyColumn("to_do", "w-[7%]", doneLabel);
    $[9] = buildEditableColumn;
    $[10] = buildReadonlyColumn;
    $[11] = doneLabel;
    $[12] = hasRole;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  const toDoColumn = t5;
  let t6;
  let t7;
  if ($[14] !== buildReadonlyColumn) {
    t6 = buildReadonlyColumn("validation_date", "w-[10%]", _temp4);
    t7 = buildReadonlyColumn("bank_account_date", "w-[11%]", _temp5);
    $[14] = buildReadonlyColumn;
    $[15] = t6;
    $[16] = t7;
  } else {
    t6 = $[15];
    t7 = $[16];
  }
  let t8;
  if ($[17] !== label) {
    t8 = label("operation_reason");
    $[17] = label;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  let t9;
  if ($[19] !== t8) {
    t9 = {
      header: t8,
      field: "operation_reason",
      sortable: true,
      className: "w-[23%]",
      cellClassName: _temp6,
      cell: _temp7
    };
    $[19] = t8;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  let t10;
  let t11;
  if ($[21] !== buildReadonlyColumn) {
    t10 = buildReadonlyColumn("account", "w-[10%]", _temp8);
    t11 = buildReadonlyColumn("amount", "w-[10%]", _temp9);
    $[21] = buildReadonlyColumn;
    $[22] = t10;
    $[23] = t11;
  } else {
    t10 = $[22];
    t11 = $[23];
  }
  let t12;
  let t13;
  if ($[24] !== buildEditableColumn) {
    t12 = buildEditableColumn("invoice_reference", "w-[11%]", _temp10);
    t13 = buildEditableColumn("comment", "w-[12%]", _temp11);
    $[24] = buildEditableColumn;
    $[25] = t12;
    $[26] = t13;
  } else {
    t12 = $[25];
    t13 = $[26];
  }
  let t14;
  if ($[27] !== label) {
    t14 = label("last_action");
    $[27] = label;
    $[28] = t14;
  } else {
    t14 = $[28];
  }
  let t15;
  if ($[29] !== onOpenHistory || $[30] !== t) {
    t15 = row_11 => <div className="flex justify-center"><button type="button" aria-label={t("paymentsReceived.actions.history")} title={t("paymentsReceived.actions.history")} className="text-[#7d828a] transition-colors hover:text-primary dark:text-white" onClick={() => onOpenHistory(row_11)}><IconHistory size={18} /></button></div>;
    $[29] = onOpenHistory;
    $[30] = t;
    $[31] = t15;
  } else {
    t15 = $[31];
  }
  let t16;
  if ($[32] !== t14 || $[33] !== t15) {
    t16 = {
      header: t14,
      field: "actions",
      sortable: false,
      className: "w-[6%]",
      cell: t15
    };
    $[32] = t14;
    $[33] = t15;
    $[34] = t16;
  } else {
    t16 = $[34];
  }
  let t17;
  if ($[35] !== t10 || $[36] !== t11 || $[37] !== t12 || $[38] !== t13 || $[39] !== t16 || $[40] !== t6 || $[41] !== t7 || $[42] !== t9 || $[43] !== toDoColumn) {
    t17 = [t6, t7, t9, t10, t11, t12, t13, toDoColumn, t16];
    $[35] = t10;
    $[36] = t11;
    $[37] = t12;
    $[38] = t13;
    $[39] = t16;
    $[40] = t6;
    $[41] = t7;
    $[42] = t9;
    $[43] = toDoColumn;
    $[44] = t17;
  } else {
    t17 = $[44];
  }
  return t17;
};
function _temp(value) {
  return <CellTooltip value={value}><span className="block truncate text-xs text-black dark:text-white">{value}</span></CellTooltip>;
}
function _temp2(value_0, isEditable) {
  return <CellTooltip value={value_0}><span className="flex w-full items-start gap-1"><span className={`min-w-0 flex-1 ${WRAPPED_TEXT}`}>{value_0}</span>{isEditable && <IconPencil size={16} className="shrink-0 text-danger/70" aria-hidden={true} />}</span></CellTooltip>;
}
function _temp3(row) {
  return isArchived(row) ? WRAPPED_CELL : `cursor-pointer bg-danger/5 transition-colors hover:bg-danger/15 ${WRAPPED_CELL}`;
}
function _temp4(row_4) {
  return formatPaymentDate(row_4.validation_date);
}
function _temp5(row_5) {
  return formatPaymentDate(row_5.bank_account_date);
}
function _temp6() {
  return WRAPPED_CELL;
}
function _temp7(row_6) {
  const value_1 = formatText(row_6.operation_reason);
  return <CellTooltip value={value_1}><span className={WRAPPED_TEXT}>{value_1}</span></CellTooltip>;
}
function _temp8(row_7) {
  return formatAccountLabel(row_7.bank_account?.dealership, row_7.plaque?.label);
}
function _temp9(row_8) {
  return `${formatStringAmount(row_8.amount)} €`;
}
function _temp10(row_9) {
  return formatText(row_9.invoice_reference);
}
function _temp11(row_10) {
  return formatText(row_10.comment);
}